import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

/**
 * Page-to-layout slot for the Pro mobile toolbar's right (`slot="end"`)
 * area. `ProTabLayout` owns the slot state and renders whatever a page
 * stashes via `useProToolbarEnd`. Cleared automatically when the page
 * unmounts so the slot is empty by default on routes that don't set it.
 */

interface ProToolbarSlotContextValue {
  endSlot: ReactNode;
  setEndSlot: (node: ReactNode) => void;
  /** When true, the layout pins the toolbar to the top (renders inside IonHeader). */
  sticky: boolean;
  setSticky: (sticky: boolean) => void;
}

const ProToolbarSlotContext = createContext<ProToolbarSlotContextValue | null>(
  null,
);

export const ProToolbarSlotProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [endSlot, setEndSlot] = useState<ReactNode>(null);
  const [sticky, setSticky] = useState<boolean>(false);
  return (
    <ProToolbarSlotContext.Provider
      value={{ endSlot, setEndSlot, sticky, setSticky }}
    >
      {children}
    </ProToolbarSlotContext.Provider>
  );
};

/** Read the current end-slot content (layout-side consumer). */
export const useProToolbarEndSlot = (): ReactNode => {
  const ctx = useContext(ProToolbarSlotContext);
  if (!ctx) return null;
  return ctx.endSlot;
};

/** Read whether the toolbar should be sticky (layout-side consumer). */
export const useProToolbarStickyValue = (): boolean => {
  const ctx = useContext(ProToolbarSlotContext);
  return ctx?.sticky ?? false;
};

/**
 * Page-side: render content into the toolbar's right slot. Pass `deps`
 * for state used inside `node` (mirrors `useEffect`'s dependency list).
 * Returning `null` from this hook is intentional — it's just for the
 * side-effect of writing into the slot.
 */
export const useProToolbarEnd = (
  node: ReactNode,
  deps: React.DependencyList,
) => {
  const ctx = useContext(ProToolbarSlotContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setEndSlot(node);
    return () => ctx.setEndSlot(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

/**
 * Page-side: opt in to a pinned toolbar (inside IonHeader) for the
 * page's lifetime. Cleared on unmount so other pages fall back to the
 * default scrolling toolbar.
 */
export const useProToolbarSticky = (sticky: boolean) => {
  const ctx = useContext(ProToolbarSlotContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setSticky(sticky);
    return () => ctx.setSticky(false);
  }, [ctx, sticky]);
};
